<script>
  import Layout from '/components/Layout.svelte';
  import { useForm, inertia, router, page } from '@inertiajs/svelte';

  export let content = '';
  export let relative_path = '';
  export let is_new = false;
  export let error = null;
  export let known_slugs = [];
  export let prev_file = null;
  export let next_file = null;

  $: flash = $page.props.flash || {};

  const form = useForm({
    content: content,
    relative_path: relative_path,
  });

  $: title = is_new ? 'New encyclopedia entry' : `Edit: ${relative_path.replace(/\.md$/, '')}`;

  $: lineCount = ($form.content.match(/\n/g) || []).length + 1;
  $: charCount = $form.content.length;

  // 'view' is default; 'edit' switches to raw textarea
  let activeTab = is_new ? 'edit' : 'view';

  // Convert [[Page Name]] or [[page_name]] to anchor tags.
  // Slug matching is case-insensitive; spaces → underscores.
  function wikiLinksToHtml(text) {
    return text.replace(/\[\[([^\]]+)\]\]/g, (_, label) => {
      const slug = label.trim().toLowerCase().replace(/\s+/g, '_');
      const exists = known_slugs.includes(slug);
      if (exists) {
        return `<a href="/encyclopedia/${slug}" class="wiki-link">${label.trim()}</a>`;
      } else {
        return `<a href="/encyclopedia/${slug}" class="wiki-link wiki-link-missing" title="Entry does not exist yet">${label.trim()}</a>`;
      }
    });
  }

  // Minimal markdown renderer. Runs after wikilink substitution.
  function renderMarkdown(raw) {
    const lines = raw.split('\n');
    const out = [];
    let inParagraph = false;

    function closeParagraph() {
      if (inParagraph) { out.push('</p>'); inParagraph = false; }
    }

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (/^---+$/.test(line.trim())) {
        closeParagraph();
        out.push('<hr>');
        continue;
      }

      const hMatch = line.match(/^(#{1,6})\s+(.*)/);
      if (hMatch) {
        closeParagraph();
        const level = hMatch[1].length;
        out.push(`<h${level}>${inlineFormat(hMatch[2])}</h${level}>`);
        continue;
      }

      if (line.trim() === '') {
        closeParagraph();
        continue;
      }

      if (/^[-*]\s+/.test(line)) {
        closeParagraph();
        out.push(`<li>${inlineFormat(line.replace(/^[-*]\s+/, ''))}</li>`);
        continue;
      }

      const formatted = inlineFormat(line);
      if (!inParagraph) { out.push('<p>'); inParagraph = true; }
      else { out.push('<br>'); }
      out.push(formatted);
    }
    closeParagraph();
    return out.join('');
  }

  function inlineFormat(text) {
    // Stash [[wikilinks]] so underscores inside slugs don't get italicised
    const stash = [];
    text = text.replace(/\[\[[^\]]+\]\]/g, m => { stash.push(m); return `\x00${stash.length - 1}\x00`; });

    text = text.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/\*(.+?)\*/g, '<em>$1</em>');
    text = text.replace(/_([^_\s][^_]*)_/g, '<em>$1</em>');
    text = text.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Restore stashed wikilinks
    text = text.replace(/\x00(\d+)\x00/g, (_, i) => stash[i]);
    return text;
  }

  function stripFrontmatter(text) {
    if (!text.startsWith('---\n')) return text;
    const close = text.indexOf('\n---\n', 4);
    if (close === -1) return text;
    return text.slice(close + 5);
  }

  $: renderedHtml = wikiLinksToHtml(renderMarkdown(stripFrontmatter($form.content)));

  function save() {
    if (is_new) {
      $form.post('/encyclopedia', { preserveScroll: true });
    } else {
      $form.patch(`/encyclopedia/${relative_path}`, { preserveScroll: true });
    }
  }

  function confirmDelete() {
    if (confirm(`Delete ${relative_path}? This cannot be undone.`)) {
      router.delete(`/encyclopedia/${relative_path}`);
    }
  }

  function insertWikiLink() {
    const ta = document.querySelector('.md-editor');
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = $form.content.slice(start, end);
    const inner = selected || 'Page Name';
    $form.content = $form.content.slice(0, start) + `[[${inner}]]` + $form.content.slice(end);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + 2, start + 2 + inner.length);
    }, 0);
  }
</script>

<svelte:head>
  <title>{title} — Age of Trevor Webedit</title>
</svelte:head>

<Layout {title}>
  {#if flash.notice}
    <div class="flash flash-notice">{flash.notice}</div>
  {/if}

  {#if error || $form.errors.content || $form.errors.relative_path}
    <div class="flash flash-alert">
      {error || $form.errors.content || $form.errors.relative_path}
    </div>
  {/if}

  <form on:submit|preventDefault={save} class="editor-form">
    {#if is_new}
      <div class="field">
        <label for="relative_path">
          File name
          <span class="hint">(filename only, no subdirectories, must end in .md)</span>
        </label>
        <input
          id="relative_path"
          type="text"
          bind:value={$form.relative_path}
          placeholder="my_entry.md"
          class="path-input"
          disabled={$form.processing}
        />
      </div>
    {:else}
      <div class="path-display">
        <span class="path-label">File:</span>
        <span class="path-value">encyclopedia/{relative_path}</span>
      </div>
    {/if}

    <div class="tab-bar">
      <div class="tabs">
        <button
          type="button"
          class="tab {activeTab === 'view' ? 'tab-active' : ''}"
          on:click={() => activeTab = 'view'}
        >View</button>
        <button
          type="button"
          class="tab {activeTab === 'edit' ? 'tab-active' : ''}"
          on:click={() => activeTab = 'edit'}
        >Edit</button>
      </div>

      {#if activeTab === 'edit'}
        <div class="tab-tools">
          <span class="meta-stat">{lineCount} lines · {charCount} chars</span>
          <button type="button" class="btn-tool" on:click={insertWikiLink} title="Insert wiki link at cursor">
            [[link]]
          </button>
        </div>
      {/if}
    </div>

    {#if activeTab === 'view'}
      <div class="preview-pane">
        {#if $form.content.trim()}
          <div class="preview-content">
            {@html renderedHtml}
          </div>
        {:else}
          <p class="preview-empty">Nothing to preview yet. Switch to Edit to add content.</p>
        {/if}
      </div>
    {:else}
      <textarea
        bind:value={$form.content}
        class="md-editor"
        spellcheck="true"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="sentences"
        disabled={$form.processing}
        placeholder="# Entry Title&#10;&#10;Write your encyclopedia entry here.&#10;Use [[Page Name]] to link to other entries."
      ></textarea>
    {/if}

    <div class="action-bar">
      <div class="action-left">
        <a use:inertia href="/encyclopedia" class="btn-cancel">← Back</a>
        {#if !is_new}
          <button type="button" class="btn-delete" on:click={confirmDelete} disabled={$form.processing}>
            Delete
          </button>
        {/if}
      </div>
      {#if !is_new && prev_file && next_file}
        <div class="sibling-nav">
          <a use:inertia href="/encyclopedia/{prev_file.relative_path.replace(/\.md$/, '')}" class="btn-sibling" title={prev_file.display}>← Prev</a>
          <a use:inertia href="/encyclopedia/{next_file.relative_path.replace(/\.md$/, '')}" class="btn-sibling" title={next_file.display}>Next →</a>
        </div>
      {/if}
      <button type="submit" class="btn-save" disabled={$form.processing}>
        {$form.processing ? 'Saving…' : 'Save & commit'}
      </button>
    </div>
  </form>
</Layout>

<style>
  .flash {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .flash-notice {
    background: #f0fdf4;
    border: 1px solid #86efac;
    color: #166534;
  }

  .flash-alert {
    background: #fef2f2;
    border: 1px solid #fca5a5;
    color: #dc2626;
  }

  .editor-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
  }

  .hint {
    font-weight: 400;
    color: #94a3b8;
    font-size: 0.8rem;
  }

  .path-input {
    padding: 0.5rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-family: 'Fira Code', monospace;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .path-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  .path-display {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #64748b;
    padding: 0.375rem 0;
  }

  .path-label {
    font-weight: 600;
    color: #374151;
  }

  .path-value {
    font-family: 'Fira Code', monospace;
    color: #166534;
  }

  .tab-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid #e2e8f0;
    padding-bottom: 0;
  }

  .tabs {
    display: flex;
    gap: 0;
  }

  .tab {
    padding: 0.5rem 1.25rem;
    border: none;
    background: none;
    font-size: 0.9rem;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: color 0.15s, border-color 0.15s;
  }

  .tab:hover {
    color: #1e293b;
  }

  .tab-active {
    color: #166534;
    border-bottom-color: #166534;
    font-weight: 600;
  }

  .tab-tools {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-bottom: 0.375rem;
  }

  .meta-stat {
    font-family: 'Fira Code', monospace;
    font-size: 0.8rem;
    color: #94a3b8;
  }

  .btn-tool {
    padding: 0.3rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background: white;
    font-size: 0.8rem;
    font-family: 'Fira Code', monospace;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }

  .btn-tool:hover {
    background: #f0fdf4;
    border-color: #86efac;
    color: #166534;
  }

  .preview-pane {
    min-height: 60vh;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    background: #fffdf7;
    overflow: hidden;
  }

  .preview-content {
    padding: 1.75rem 2rem;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 0.975rem;
    line-height: 1.8;
    color: #1e293b;
  }

  .preview-empty {
    padding: 2rem;
    color: #94a3b8;
    font-style: italic;
    text-align: center;
  }

  /* Rendered markdown styles */
  .preview-content :global(h1) {
    font-size: 1.6rem; font-weight: 800; margin: 0 0 0.75rem;
    color: #1e3a8a; letter-spacing: -0.02em;
  }
  .preview-content :global(h2) {
    font-size: 1.2rem; font-weight: 700; margin: 1.75rem 0 0.5rem;
    color: #1e3a8a; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.25rem;
  }
  .preview-content :global(h3) {
    font-size: 1rem; font-weight: 700; margin: 1.5rem 0 0.375rem; color: #374151;
  }
  .preview-content :global(h4),
  .preview-content :global(h5),
  .preview-content :global(h6) {
    font-size: 0.95rem; font-weight: 600; margin: 1.25rem 0 0.25rem; color: #374151;
  }
  .preview-content :global(p) { margin: 0 0 0.875rem; }
  .preview-content :global(hr) { border: none; border-top: 1px solid #e2e8f0; margin: 1.5rem 0; }
  .preview-content :global(strong) { font-weight: 700; }
  .preview-content :global(em) { font-style: italic; }
  .preview-content :global(code) {
    font-family: 'Fira Code', monospace; font-size: 0.85em;
    background: #f1f5f9; padding: 0.1em 0.35em; border-radius: 0.25rem;
  }
  .preview-content :global(li) { margin: 0.2rem 0 0.2rem 1.5rem; list-style: disc; }

  .preview-content :global(.wiki-link) {
    color: #1d4ed8;
    text-decoration: none;
    border-bottom: 1px solid #93c5fd;
  }
  .preview-content :global(.wiki-link:hover) {
    background: #eff6ff;
    border-bottom-color: #1d4ed8;
  }
  .preview-content :global(.wiki-link-missing) {
    color: #dc2626;
    border-bottom: 1px dashed #fca5a5;
  }
  .preview-content :global(.wiki-link-missing:hover) {
    background: #fef2f2;
  }

  .md-editor {
    width: 100%;
    min-height: 60vh;
    padding: 1.25rem;
    font-family: 'Fira Code', monospace;
    font-size: 0.875rem;
    line-height: 1.8;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    resize: vertical;
    outline: none;
    background: #0f172a;
    color: #e2e8f0;
    caret-color: #86efac;
    tab-size: 2;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .md-editor:focus {
    border-color: #86efac;
    box-shadow: 0 0 0 3px rgba(134, 239, 172, 0.15);
  }

  .md-editor:disabled {
    opacity: 0.6;
  }

  .action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding-top: 0.5rem;
  }

  .action-left {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .btn-cancel {
    color: #64748b;
    text-decoration: none;
    font-size: 0.9rem;
    padding: 0.5rem 0.875rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    transition: background 0.2s;
  }

  .btn-cancel:hover {
    background: #f8fafc;
  }

  .btn-delete {
    background: none;
    border: 1px solid #fca5a5;
    color: #dc2626;
    padding: 0.5rem 0.875rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-delete:hover:not(:disabled) {
    background: #fef2f2;
  }

  .btn-delete:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .sibling-nav {
    display: flex;
    gap: 0.375rem;
  }

  .btn-sibling {
    color: #64748b;
    text-decoration: none;
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    transition: background 0.2s, color 0.2s;
    white-space: nowrap;
  }

  .btn-sibling:hover {
    background: #f0fdf4;
    color: #166534;
    border-color: #86efac;
  }

  .btn-save {
    background: #166534;
    color: white;
    border: none;
    padding: 0.625rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s;
  }

  .btn-save:hover:not(:disabled) {
    background: #15803d;
    transform: translateY(-1px);
  }

  .btn-save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
